import { confirmSignIn } from 'aws-amplify/auth';
import FormText from 'components/form/FormText';
import IndicatorButton from 'components/indicator/IndicatorButton';
import { AppConst } from 'constants/appConst';

import { useAppState } from 'hooks/useAppState';
import { useEffect, useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompanyRegistration } from './hooks/useCompanyRegistration';
import { isEmpty, submit } from 'utils/app';

/**
 * ログイン認証コード入力画面
 */
export default function SigninAuth(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    confirmationCode: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const location = useLocation();

  const [processing, setProcessing] = useState(false);
  const { initializeAcctState } = useAppState();
  const { registerCompany } = useCompanyRegistration();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // パラメタの検証
    if (isEmpty(location.state?.mailAddress ?? null)) {
      navigate(-1);
    }
    if (isEmpty(location.state?.nextTo ?? null)) {
      navigate(-1);
    }
  }, [location.state, navigate]);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 確認ボタン押下時の処理
   */
  const onConfirm = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // 認証コードの検証
          const response = await confirmSignIn({
            challengeResponse: data.confirmationCode,
          });

          if (!response.isSignedIn) {
            setError('confirmationCode', {
              type: 'custom',
              message: '認証コードが間違っています。',
            });
            return;
          }

          // サインアップの場合は企業情報登録
          if (location.state?.signupData) {
            await registerCompany(location.state.signupData);
          }

          // ステート情報の更新と画面遷移
          await initializeAcctState();
          navigate(location.state?.nextTo);
        } catch (e: unknown) {
          // 認証コードの失効、または間違い上限回数に達した場合
          if (e instanceof Error && e.name === 'NotAuthorizedException') {
            setError('confirmationCode', {
              type: 'custom',
              message:
                'セッションが失効しました。前の画面に戻って認証コードの発行からやり直してください。',
            });
          } else if (e instanceof Error && e.name === 'CompanyNameAndTelDuplicated') {
            // 企業名と電話番号の重複エラーの場合、エラー情報と入力データを保存して前の画面に戻る
            sessionStorage.setItem('duplicatedError', e.message);
            sessionStorage.setItem('signupFormData', JSON.stringify(location.state.signupData));
            navigate(-1);
          } else {
            showBoundary(e);
          }
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-login">
        <div className="container">
          <div className="card card-login">
            <div className="card-body">
              <h1 className="card-title">認証コードを送信しました</h1>
              <p className="lead">
                {location.state?.mailAddress}
                に認証コードを送信しました。メールに記載されたコードを入力してください。
              </p>

              <form className="form-login">
                <div>
                  <FormText
                    subject="認証コード"
                    name="confirmationCode"
                    register={register}
                    errors={errors}
                    required={true}
                    format="numeric"
                    minLength={6}
                    maxLength={6}
                    requiredMark={true}
                  />
                </div>
                <div className="form-footer">
                  <button
                    type="button"
                    className="btn btn-submit w-100"
                    onClick={onConfirm}
                    disabled={processing}
                  >
                    <IndicatorButton visible={processing} />
                    コードの確認
                  </button>
                  <p className="mt-3">
                    <label className="form-label">
                      <a onClick={() => navigate(-1)}>前の画面に戻る</a>
                    </label>
                  </p>
                </div>
              </form>
            </div>
          </div>
          <div className="text-center">
            <a href={AppConst.url.privacy} target="_blank" className="caption">
              privacy &amp; Terms
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
